<script setup lang="ts">
import { onBeforeUnmount, onMounted, provide, readonly, ref, watch } from "vue";
import { RouterView, useRoute } from "vue-router";

import ModalDialog from "@/components/ui/ModalDialog.vue";
import ToastNotificationArea from "@/components/ui/ToastNotificationArea.vue";
import { lastErrorKey, raiseErrorKey, type ErrorCode } from "@/injectionKeys";
import { useAuthStore } from "@/stores/auth";

let lastHiddenAt: Date | null = null;

const lastError = ref<ErrorCode>();

const route = useRoute();
const auth = useAuthStore();

function raiseError(code: ErrorCode) {
  lastError.value = code;
}
async function onVisibilityChange() {
  if (document.visibilityState === "hidden") {
    // user as switched to another tab
    lastHiddenAt = new Date();
  } else {
    // user has returned to the tab
    if (lastHiddenAt) {
      const timeSinceHidden = new Date().getTime() - lastHiddenAt.getTime();
      if (timeSinceHidden > 1000 * 60 * 59) {
        // user has been hidden for more than 59 minutes
        // artefacts URL have expired
        // so we need to refresh the page to get new URLs
        window.location.reload();
      } else {
        await auth.ensureUserIsAuthenticated();
      }
    }
  }
}

provide(raiseErrorKey, raiseError);
provide(lastErrorKey, readonly(lastError));

onMounted(() => {
  document.addEventListener("visibilitychange", onVisibilityChange);
});
onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", onVisibilityChange);
});

watch(
  () => route.fullPath,
  () => {
    lastError.value = undefined;
  }
);
</script>

<template>
  <div class="content">
    <div class="shell">
      <RouterView />
    </div>
    <div id="below-modals-layer" class="layer teleport-overlay" />
    <div class="layer modal">
      <ModalDialog />
    </div>
    <div class="layer toast">
      <ToastNotificationArea />
    </div>
  </div>
</template>

<style scoped>
.content {
  color: var(--content-primary);

  .shell {
    position: relative;
    z-index: 0;
  }

  .layer {
    position: fixed;
    inset: 0;
    pointer-events: none;

    &.teleport-overlay {
      z-index: 8000;
    }

    &.toast {
      z-index: 9500;
    }

    &.modal {
      z-index: 9000;
    }
  }
}
</style>
